import { IBooking, MaxDuration } from '@api'
import { findKey } from 'lodash'
import moment from 'moment-timezone'
import { DateUtil, TIME } from './dateUtil'
import { RecurrenceT, TypePattern } from '@providers'

// DATE_PARSE_FORMAT specifically helps Firefox
export const DATE_PARSE_FORMAT = 'yyyy-MM-dd hh:mm:ss'

export const parseDate = (date: Date, timezone: string) =>
  moment.utc(date, DATE_PARSE_FORMAT).tz(timezone)

export const parseDate2 = (date: Date, timezone: string) => DateUtil.utc(date, timezone)

export const formatDate = (d: moment.Moment) =>
  moment(d)
    .utc()
    .format(DATE_PARSE_FORMAT + ' UTC')
export const formatDate2 = (d: DateUtil, formatString?: string) =>
  d.utc().format(formatString || DATE_PARSE_FORMAT) + ' UTC'

// export const formatDate = (d: (string | Date) | undefined) =>
//   DateUtil(d).utc().format(DATE_PARSE_FORMAT)

export const convertDateTimeToTimezone = ({
  dateTime,
  timezone,
}: {
  dateTime: moment.Moment
  timezone: string
}) => {
  const tempDate = moment(dateTime)
  return convertDateToTimezone({
    dateTime: tempDate,
    year: tempDate.year(),
    month: tempDate.month(),
    date: tempDate.date(),
    hour: tempDate.hour(),
    minute: tempDate.minute(),
    second: tempDate.second(),
    timezone,
  })
}

export const convertDateToTimezone = ({
  dateTime,
  hour = dateTime.hour(),
  minute = dateTime.minute(),
  second = dateTime.second(),
  year = dateTime.year(),
  month = dateTime.month(),
  date = dateTime.date(),
  timezone,
}: {
  dateTime: moment.Moment
  hour: number
  minute: number
  second: number
  year: number
  month: number
  date: number
  timezone: string
}) =>
  moment.tz(dateTime, timezone).clone().set({
    year,
    month,
    date,
    hour,
    minute,
    second,
  })

export const convertDateToTimezone2 = ({
  dateTime,
  timezone,
}: {
  dateTime: DateUtil
  timezone: string
}) =>
  DateUtil.tz(timezone).set({
    year: dateTime.year(),
    month: dateTime.month(),
    date: dateTime.date(),
    hour: dateTime.hours(),
    minute: dateTime.minutes(),
    second: dateTime.seconds(),
  })

export function datePickerFormatter(format: string) {
  return {
    formatDate: (date: Date, locale: string) => {
      return locale ? DateUtil(date).locale(locale).format(format) : DateUtil(date).format(format)
    },
    parseDate: (str: string, locale = 'en'): Date | null => {
      if (!str) return null
      return DateUtil(str).locale(locale).toDate()
    },
    maxDate: DateUtil().add(15, TIME.YEARS).toDate(),
  }
}

type GetStartAndEndDateParams = {
  startDate: DateUtil
  endDate?: DateUtil
  defaultTimeFrom: string
  defaultTimeTo: string
  allDay: boolean
  timezone: string
  recurrence?: RecurrenceT
}

export const getDefaultTimeValues = (param: {
  bookingWorkdayTimeFrom: string
  bookingWorkdayTimeTo: string
}) => {
  const df = (param.bookingWorkdayTimeFrom || '0:0').split(':')
  const dt = (param.bookingWorkdayTimeTo || '0:15').split(':')
  return {
    defaultHourFrom: Number(df[0]),
    defaultMinuteFrom: Number(df[1]),
    defaultHourTo: Number(dt[0]),
    defaultMinuteTo: Number(dt[1]),
  }
}

export const getStartAndEndDate = ({
  timezone,
  startDate = DateUtil.tz(timezone),
  endDate = startDate.clone(),
  defaultTimeFrom,
  defaultTimeTo,
  allDay,
  recurrence,
}: GetStartAndEndDateParams) => {
  const df = defaultTimeFrom.split(':').map((e) => Number(e))
  const dt = defaultTimeTo.split(':').map((e) => Number(e))
  const defaultTimeFromObj = startDate.clone().set({ hour: df[0], minute: df[1] })
  const defaultTimeToObj = endDate.clone().set({ hour: dt[0], minute: dt[1] })
  const now = DateUtil.tz(timezone)
  let fromDate = startDate
  let toDate = endDate

  if (
    now.isSameOrAfter(defaultTimeToObj) ||
    (now.isSameOrAfter(toDate) && toDate.isSameOrAfter(defaultTimeToObj))
  ) {
    fromDate = defaultTimeFromObj.clone().add(1, TIME.DAYS)
    toDate =
      allDay && recurrence?.typePattern !== TypePattern.NEVER
        ? defaultTimeToObj.clone().add(1, TIME.DAYS)
        : fromDate.clone().add(15, TIME.MINUTES)
  } else {
    fromDate = now.isBefore(defaultTimeFromObj)
      ? fromDate.isBetween(defaultTimeFromObj, defaultTimeToObj) && !allDay
        ? fromDate
        : defaultTimeFromObj.clone()
      : now.isSameOrAfter(fromDate)
      ? closest15minutes(now)
      : fromDate
    if (fromDate.isAfter(defaultTimeToObj)) {
      fromDate
        .add(1, TIME.DAYS)
        .set({ hour: defaultTimeFromObj.hours(), minute: defaultTimeFromObj.minutes() })
    }

    if (toDate.isSameOrAfter(defaultTimeToObj)) {
      if (
        toDate.isSame(defaultTimeToObj, TIME.DAYS) &&
        recurrence?.typePattern !== TypePattern.NEVER
      ) {
        toDate = allDay
          ? toDate.add(1, TIME.DAYS)
          : defaultTimeToObj
              .clone()
              .add(1 + 15 / (24 * 60), TIME.DAYS)
              .set({ hour: df[0], minute: df[1] })
      } else {
        toDate =
          allDay && recurrence?.typePattern !== TypePattern.NEVER
            ? toDate.set({ hour: defaultTimeToObj.hours(), minute: defaultTimeToObj.minutes() })
            : toDate
      }
    } else if (toDate.isSame(defaultTimeToObj, TIME.DAYS) && toDate.isAfter(now, TIME.DAYS)) {
      toDate =
        allDay && recurrence?.typePattern !== TypePattern.NEVER
          ? defaultTimeToObj
          : toDate.isBetween(
              defaultTimeToObj.clone().set({ hour: df[0], minute: df[1] }),
              defaultTimeToObj
            )
          ? toDate
          : toDate.clone().set({ hour: df[0], minute: df[1] + 15 })
    } else {
      if (allDay && toDate.isSame(defaultTimeToObj, [TIME.HOURS, TIME.MINUTES])) {
        toDate.set({ hour: dt[0], minute: dt[1] })
      }
      if (toDate.isMidNight()) {
        toDate.set({ hour: allDay ? dt[0] : df[0], minute: allDay ? dt[1] : df[1] + 15 })
      }
    }
  }
  if (fromDate.isSameOrAfter(toDate)) {
    toDate = allDay
      ? fromDate.clone().set({ hour: dt[0], minute: dt[1] })
      : closest15minutes(fromDate)
  }

  fromDate.reset(TIME.SECONDS)
  toDate.reset(TIME.SECONDS)

  return {
    fromDate,
    toDate,
    // toDate: (endDate.diff(startDate, TIME.DAYS) > 1
    //   ? toDate.set({ date: endDate.date(), month: endDate.month(), year: endDate.year() })
    //   : toDate
    // ).reset(TIME.SECONDS),
  }
}

// export const to24HourFormatStr = (date: string) => mz(date, 'h:mm a').format('HH:mm')
export const to24HourFormatStr = (date: string) => {
  return DateUtil.parse(date, 'h:mm aa').format('HH:mm')
}
// export const humanReadableDiff = (diff: DateUtil) => {
//     const years = diff.years()
//     const months = diff.months()
//     const days = diff.days()
//     const result = []

//     if (years) result.push(`${years} year${years === 1 ? '' : 's'}`)
//     if (months) result.push(`${months} month${months === 1 ? '' : 's'}`)
//     if (days) result.push(`${days} day${days === 1 ? '' : 's'}`)
//     return result.join(', ')
//   }

const _getMaxDateByTimezone = (
  timezone: string,
  maxFutureBookingDuration: MaxDuration
): DateUtil => {
  const duration = getTimeTypeAndDaxFutureBookingDuration(maxFutureBookingDuration)
  return DateUtil.tz(timezone).add(duration.value, duration.key)
}

export const getMaxDateByTimezone = (
  timezone: string,
  maxFutureBookingDuration: MaxDuration
): string => _getMaxDateByTimezone(timezone, maxFutureBookingDuration).format('MMM d')

export const createDisabledDate = (timezone: string, maxFutureBookingDuration: MaxDuration) =>
  _getMaxDateByTimezone(timezone, maxFutureBookingDuration)

type ConvertToTimezoneParam = {
  dateTime: string
  timezone: string
}

const getTimeTypeAndDaxFutureBookingDuration = (maxFutureBookingDuration: MaxDuration) => {
  const key = findKey<MaxDuration>(maxFutureBookingDuration)

  return {
    key: key as TIME,
    value: (maxFutureBookingDuration[key as keyof MaxDuration] as number) || 0,
  }
}

export const dateToEndOfDay = ({
  dateTime,
  timezone,
  time: { hour = 23, minute = 59 },
}: {
  dateTime: DateUtil
  timezone: string
  time: { hour: number; minute: number }
}) =>
  (timezone ? convertDateToTimezone2({ dateTime, timezone }) : dateTime.clone()).set({
    hour,
    minute,
  })

// export const convertDateTimeToTimezone = ({ dateTime, timezone }) => {
//   const tempDate = moment(dateTime)
//   return convertDateToTimezone({
//     dateTime: tempDate,
//     year: tempDate.year(),
//     month: tempDate.month(),
//     date: tempDate.date(),
//     hour: tempDate.hour(),
//     minute: tempDate.minute(),
//     second: tempDate.second(),
//     timezone,
//   })
// }

//   export const setDateTimeFromDate = ({ dateTime, fromDate }) =>
//     moment(dateTime)
//       .clone()
//       .set({
//         hour: fromDate.hour(),
//         minute: fromDate.minute(),
//         second: fromDate.second(),
//         year: fromDate.year(),
//         month: fromDate.month(),
//         date: fromDate.date(),
//       })

export const createProperDate = (date: DateUtil) => date.clone()

// new Date(date.year(), date.month(), date.date(), date.hours(), date.minutes(), date.seconds())

export const closest15minutes = (date: DateUtil) => {
  // const mins = (Math.floor(date.minutes() / 15) + 1) * 15
  // return date
  //   .clone()
  //   .add(mins - date.minutes(), TIME.MINUTES)
  //   .reset(TIME.SECONDS)
  return date.minutes() % 15 !== 0 && date.minutes() !== 0
    ? date
        .clone()
        .add(15 - (date.minutes() % 15), TIME.MINUTES)
        .reset(TIME.SECONDS)
    : date.clone().add(15, TIME.MINUTES)
}

export const isMultiDayBooking = (booking: IBooking) =>
  !DateUtil(booking.startDate).isSame(DateUtil(booking.endDate), TIME.DAYS)

export const isRecurringBooking = (booking: IBooking) =>
  booking.bookingSchedule && booking.bookingSchedule.pattern.type !== TypePattern.NEVER

export const dateToString = (d: (string | Date) | undefined) => DateUtil(d).format('ddd, MMM DD')

const formatDateString = 'MMM DD, h:mm a'

export const getFullBookingDates = (startDate: DateUtil, endDate: DateUtil) =>
  `${startDate.format(formatDateString)} - ${endDate.format(formatDateString)}`

type GetFormattedHoursT = (
  bookingWorkdayTimeFrom: string,
  bookingWorkdayTimeTo: string,
  timezone?: string
) => { timeFrom: DateUtil; timeTo: DateUtil }

export const getDefaultTimes: GetFormattedHoursT = (
  bookingWorkdayTimeFrom,
  bookingWorkdayTimeTo,
  timezone
) => {
  const dates = getDefaultTimeValues({ bookingWorkdayTimeFrom, bookingWorkdayTimeTo })

  return {
    timeFrom: DateUtil.tz(timezone).set({
      hour: dates.defaultHourFrom,
      minute: dates.defaultMinuteFrom,
      second: 0,
      millisecond: 0,
    }),
    timeTo: DateUtil.tz(timezone).set({
      hour: dates.defaultHourTo,
      minute: dates.defaultMinuteTo,
      second: 0,
      millisecond: 0,
    }),
  }
}
